import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNumber } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Copy, FileSearch } from "lucide-react";
import { toast } from "sonner";

/**
 * GL Entry viewer opened via `<a href="/gl-entry/:id" target="_blank">` from
 * fraud-fingerprint drill-downs. Standalone page — mirrors the deep-link
 * pattern Planning Forms uses (`/planning?openForm=<id>`) but path-based so
 * a raw browser tab opens directly on the entry.
 */
export default function GLEntryView() {
  const { t, lang, dict } = useI18n();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!id) return;
    api.get(`/gl-entry/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(toErrMsg(e, t("glEntry.notFound"))));
  }, [id, t]);

  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(String(v))
      .then(() => toast.success(t("glEntry.copied")), () => toast.error(t("glEntry.copyFailed")));
  };

  if (err) {
    return (
      <div className="p-6" data-testid="gl-entry-error">
        <Card><CardContent className="p-8 text-center">
          <FileSearch className="mx-auto h-8 w-8 text-slate-400" />
          <div className="mt-2 text-sm text-red-600">{err}</div>
          <Link to="/gl-import" className="mt-4 inline-block text-xs text-emerald-600 hover:underline">
            {t("glEntry.back")}
          </Link>
        </CardContent></Card>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-sm text-muted-foreground">{t("glEntry.loading")}</div>;

  const e = data.entry || {};
  const eng = data.engagement || {};
  const amt = Number(e.debit || 0) - Number(e.credit || 0);
  const L = dict.glEntry?.labels || {};
  const rows = [
    [L.entryId || "Entry ID", e.id, true],
    [L.voucher || "Voucher / UIN", e.voucher_no || "—"],
    [L.entryDate || "Entry date", e.entry_date || "—"],
    [L.accountCode || "Account code", e.account_code || "—"],
    [L.accountName || "Account name", e.account_name || "—"],
    [L.debit || "Debit", fmtNumber(e.debit || 0, lang)],
    [L.credit || "Credit", fmtNumber(e.credit || 0, lang)],
    [L.net || "Net (Dr − Cr)", fmtNumber(amt, lang)],
    [L.description || "Description", e.description || "—"],
    [L.vendorCustomer || "Vendor / customer", e.vendor_customer || "—"],
    [L.categoryHint || "Category hint", e.category_hint || "—"],
    [L.postedBy || "Posted by", e.posted_by || "—"],
    [L.postedTime || "Posted time", e.posted_time || "—"],
    [L.period || "Period", e.period || "current"],
  ];

  return (
    <div className="space-y-4 p-6" data-testid="gl-entry-page">
      <div className="flex items-center gap-3">
        <Link to="/gl-import"
              className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-slate-50 dark:hover:bg-slate-900"
              data-testid="gl-entry-back">
          <ArrowLeft className="h-3 w-3" /> {t("b3.glImport")}
        </Link>
        <div className="text-xs text-muted-foreground">
          {(eng.engagement_name || eng.client_name) && <span className="font-medium text-foreground">{eng.engagement_name || eng.client_name}</span>}
          {eng.firm_engagement_ref && <span> · {eng.firm_engagement_ref}</span>}
          {eng.financial_year && <span> · FY {eng.financial_year}</span>}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span>{t("glEntry.title")}</span>
            {e.voucher_no && (
              <Badge variant="outline" className="font-mono text-[10px]">{e.voucher_no}</Badge>
            )}
            {eng.currency && (
              <Badge variant="outline" className="ms-auto text-[10px]">{eng.currency}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {rows.map(([label, value, mono]) => (
              <div key={label} className="flex items-start justify-between gap-3 rounded-md border bg-slate-50 p-3 dark:bg-slate-900/40">
                <div className="min-w-0 flex-1">
                  <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
                  <dd className={`mt-0.5 break-words ${mono ? "font-mono text-[11px]" : "text-sm"}`}
                      data-testid={`gl-entry-field-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    {value}
                  </dd>
                </div>
                {value && value !== "—" && (
                  <button
                    type="button"
                    onClick={() => copy(value)}
                    className="mt-4 inline-flex h-6 w-6 flex-none items-center justify-center rounded border text-muted-foreground hover:bg-white hover:text-foreground dark:hover:bg-slate-800"
                    title={t("glEntry.copy")}>
                    <Copy className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
